import { es, enUS, zhCN } from "date-fns/locale";
import {
  BankAccountNumberByTransactionType,
  DestinationBankAccountClassName,
  RouterSupportedFiat,
  RouterTransferMethodsTitle,
  SyntheticRouterSupportedFiat,
  TransactionTypeOptions,
} from "../interfaces/enums";
import {
  IDestinationBankAccountAll,
  IDestinationBankAccountUSRouter,
  IError,
  ISelectOption,
} from "../interfaces";
import { useFormContext } from "react-hook-form";
import { IRouterCreateBankAcc } from "interfaces/router";
import { isWeekend, startOfDay, subDays } from "date-fns";
import { Locale } from "react-day-picker";

export const findOption = (value: any, options: Array<ISelectOption>) =>
  options?.find((option) =>
    option.value === value ? { label: option, value: option } : null,
  );

export const valueLabelMapper = <T,>(
  options: Array<string | T>,
  t?: (value: string) => string,
) => {
  return options.map((option) => ({
    label: typeof option === "string" ? (t ? t(option) : option) : "-",
    value: option,
  }));
};
const pad = (num: number) => (num < 10 ? `0${num}` : `${num}`);

export const toIsoString = (date: Date) => {
  const tzo = -date.getTimezoneOffset();
  const dif = tzo >= 0 ? "+" : "-";

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate(),
  )} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(
    date.getSeconds(),
  )}${dif}${pad(Math.floor(Math.abs(tzo) / 60))}:${pad(Math.abs(tzo) % 60)}`;
};

export const localeSelector = (lang?: string): Locale => {
  if (lang === "es") return es;
  if (lang === "zh") return zhCN;
  else return enUS;
};

export const langSelector = (lang?: string): "en" | "es" | "zh" => {
  return lang?.toLocaleLowerCase().substring(0, 2) as "en" | "es" | "zh";
};

export const generateAccountAlias = (account: IDestinationBankAccountAll) => {
  if (account.class_name === DestinationBankAccountClassName.SPID)
    return {
      label: `${account.recipient_name} - ${account.bank_name} - ${
        account.class_name &&
        BankAccountNumberByTransactionType[account.class_name]
      }: ${account.clabe_dest} - ${
        account.class_name && TransactionTypeOptions[account.class_name]
      }`,
      value: account.id,
    };

  if (account.class_name === DestinationBankAccountClassName.UsDomesticWire) {
    return {
      label: `${account.recipient_name} - ${account.bank_name} - ${
        account.class_name &&
        BankAccountNumberByTransactionType[account.class_name]
      }: ${account.acct_number_dest} - ${
        account.class_name && TransactionTypeOptions[account.class_name]
      }`,
      value: account.id,
    };
  }

  if (account.class_name === DestinationBankAccountClassName.CryptoWallet) {
    return {
      label: `${account.alias} - ${account.network} - ${account.wallet_address}`,
      value: account.id,
    };
  }
};

export const generateOnlyAlias = (account: IRouterCreateBankAcc) => {
  return [
    account.account_name,
    account.bank_name,
    account.account_number,
    account.transfer_method,
    account.class_name,
  ]
    .filter((f) => f)
    .join(" - ");
};

export const generateAccountAliasRouter = (account: IRouterCreateBankAcc) => {
  return {
    label: [
      account.alias,
      account.account_name,
      account.bank_name,
      account.account_number,
      RouterTransferMethodsTitle[account.transfer_method],
      account.fiat,
      account.memo,
    ]
      .filter((f) => f)
      .join(" - "),
    value: account.id,
  };
};

export const generateAccountAliasRouterUS = (
  account: IDestinationBankAccountUSRouter,
) => {
  return {
    label: `${account.account_name} - ${account.bank_name} - ${
      account.account_number
    } - ${account.account_type} - ${
      RouterTransferMethodsTitle[account.transfer_method]
    }`,
    value: account.id,
  };
};

export const emptyValue = "—";

export const transformErrorResponse = (error: any | IError) => {
  // Missing fields type error
  if (Array.isArray(error?.data?.detail)) {
    const { loc, msg /*type*/ } = error.data.detail[0];
    return `${msg} ${loc?.[1] || ""}`;
  }
  return (
    error?.data?.detail! ||
    // the compliance engine's error shape ({ error: "..." })
    error?.data?.error ||
    error?.data ||
    error ||
    "An error occurred. Please notifiy Crosspoint."
  );
};

export const removeUndefinedValues = <T extends Record<string, any>>(
  obj: T,
): T => {
  return Object.fromEntries(
    Object.entries(obj).filter(
      ([_, value]) => value !== undefined && value !== "" && value !== null,
    ),
  ) as T;
};

/**
 * Builds a URL query string with regular params plus repeated `filter` params.
 * Each entry in `filter` is appended separately, e.g.
 * `filter=alias:foo&filter=country:MX`.
 */
export const buildUrlWithFilters = (
  baseUrl: string,
  { filter, ...params }: Record<string, any> & { filter?: string[] },
): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  (filter ?? []).forEach((f) => {
    if (f) searchParams.append("filter", f);
  });

  const queryString = searchParams.toString();

  return queryString ? `${baseUrl}?${queryString}` : baseUrl;
};

/** A single backend filter entry built from a known set of fields. */
export type FilterEntry<Field extends string> = `${Field}:${string}`;

/**
 * Type-safe builder for backend `filter=field:value` entries.
 * Pass the allowed fields as a type argument so only valid fields compile:
 *
 * ```ts
 * const f = makeFilterBuilder<"alias" | "bank_name" | "account_name">();
 * f("alias", "foo"); // "alias:foo"
 * f("nope", "foo"); // compile error
 * ```
 */
export const makeFilterBuilder =
  <Field extends string>() =>
  (field: Field, value: string): FilterEntry<Field> =>
    `${field}:${value}`;

export const useTrimmer = () => {
  const { setValue, getValues } = useFormContext();
  const onBlur = (field: any) => {
    setValue(field.target.name, getValues(field.target.name)?.trim());
  };

  return { onBlur };
};

export const transformToValidFiat = (fiat: SyntheticRouterSupportedFiat) => {
  if (fiat === SyntheticRouterSupportedFiat["MexDolar"]) {
    return RouterSupportedFiat.USD;
  }
  return fiat;
};

export function deeplyTrim(obj: unknown): unknown {
  const seen = new WeakSet();

  function helper(value: unknown): unknown {
    if (typeof value === "string") {
      return value.trim();
    } else if (Array.isArray(value)) {
      return value.map(helper);
    } else if (value !== null && typeof value === "object") {
      if (seen.has(value)) {
        return value; // Avoid circular reference
      }
      seen.add(value);
      return Object.fromEntries(
        Object.entries(value).map(([key, val]) => [key, helper(val)]),
      );
    }
    return value;
  }

  return helper(obj);
}

export function keysAsList(obj: any) {
  return Object.keys(obj).map((key) => obj[key]);
}

export function getPreviousBusinessDay(date: Date) {
  let previousDay = subDays(date, 1);

  // If the previous day is a weekend, adjust to the preceding Friday
  if (isWeekend(previousDay)) {
    previousDay =
      previousDay.getDay() === 0
        ? subDays(previousDay, 2)
        : subDays(previousDay, 1);
  }

  return startOfDay(previousDay);
}
